import { useEffect, useState, useCallback } from "react";
import { Quote } from "lucide-react";

const reviews = [
  {
    title: "AI Engineer",
    quote:
      "I can now pass my OpenAI key to Codex without worrying about rotation — Double O handles it all behind the scenes.",
  },
  {
    title: "Product Manager",
    quote:
      "Our team ships faster because engineers aren't blocked waiting for API keys. I just grant access through Double O and they're off.",
  },
  {
    title: "Head of HR",
    quote:
      "Onboarding new hires used to mean Slack DMs full of secrets. Now I just add them to a Double O team and they have what they need, securely.",
  },
  {
    title: "DevOps Lead",
    quote:
      "We went from .env files scattered across repos to a single source of truth. Our Claude Code agents pull secrets on the fly with short-lived tokens.",
  },
  {
    title: "Full-Stack Developer",
    quote:
      "I use the proxy feature to call third-party APIs from my Cursor agent without ever exposing the raw key. It's a game changer.",
  },
  {
    title: "Security Engineer",
    quote:
      "Finally, a secrets manager that makes compliance easy. Audit logs, IP whitelisting, auto-expiring tokens — everything I need in one place.",
  },
];

export default function ReviewBar() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goTo = useCallback((index: number) => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentIndex(index);
      setIsTransitioning(false);
    }, 500);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      goTo((currentIndex + 1) % reviews.length);
    }, 8000);

    return () => clearInterval(interval);
  }, [currentIndex, goTo]);

  // Get 3 visible reviews starting from currentIndex
  const visibleReviews = [0, 1, 2].map(
    (offset) => reviews[(currentIndex + offset) % reviews.length]
  );

  return (
    <div className="w-full py-10">
      <div
        className={`transition-all duration-500 ${
          isTransitioning
            ? "opacity-0 translate-y-2"
            : "opacity-100 translate-y-0"
        }`}
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 px-6">
          {visibleReviews.map((review, i) => (
            <div key={`${currentIndex}-${i}`} className="flex flex-col items-center text-center">
              <Quote className="w-6 h-6 text-primary/40 mb-3" />
              <blockquote className="text-sm sm:text-base text-foreground/90 italic leading-relaxed mb-4">
                "{review.quote}"
              </blockquote>
              <p className="text-sm text-muted-foreground">{review.title}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-center gap-2 mt-8">
        {reviews.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              i === currentIndex
                ? "bg-primary w-6"
                : "bg-muted-foreground/30 hover:bg-muted-foreground/50"
            }`}
            aria-label={`Go to review ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
}
